import React from 'react';
import { Phone, Wrench, Shield, CheckCircle, Clock, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import VillesVoisines from '../components/VillesVoisines';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';

import SeoNav from '../components/seo/SeoNav';
import CheckList from '../components/seo/CheckList';
import FAQSection from '../components/seo/FAQSection';
import AvisSection from '../components/seo/AvisSection';
import { CtaSection, SeoFooter } from '../components/seo/CtaFooter';

const FAQ = [
  { q: "Combien de temps prend une réparation d'iPhone à Saint-Chamond ?", r: "La plupart de nos réparations iPhone (écran, batterie, connecteur de charge) sont effectuées en 30 à 60 minutes. Pour des réparations plus complexes comme les dégâts des eaux ou les problèmes de carte mère, le délai peut être de quelques heures à 24h maximum." },
  { q: "Quels modèles d'iPhone réparez-vous ?", r: "Nous réparons tous les modèles d'iPhone : iPhone 15 Pro Max, iPhone 15, iPhone 14, iPhone 13, iPhone 12, iPhone 11, iPhone XR, iPhone XS, iPhone X, iPhone SE (2020/2022), iPhone 8, iPhone 7 et même les modèles plus anciens." },
  { q: "Les pièces de remplacement sont-elles de bonne qualité ?", r: "Oui, nous utilisons uniquement des pièces premium de haute qualité : écrans OLED, batteries haute capacité, et composants testés. Toutes nos pièces sont vérifiées avant et après installation. Nous proposons également des pièces d'origine Apple sur demande." },
  { q: "Proposez-vous une garantie sur les réparations iPhone ?", r: "Absolument ! Toutes nos réparations iPhone sont garanties 3 mois, pièces et main d'œuvre. Si vous rencontrez le moindre problème avec la réparation effectuée, nous intervenons gratuitement pendant toute la période de garantie." },
  { q: "Combien coûte une réparation d'écran iPhone à Saint-Chamond ?", r: "Le tarif varie selon le modèle d'iPhone et la qualité d'écran choisie. Nous proposons toujours un devis gratuit et immédiat avant intervention : appelez-nous au 07.73.60.15.21 ou écrivez-nous sur WhatsApp." },
  { q: "Puis-je venir directement sans rendez-vous ?", r: "Oui, vous pouvez passer directement, mais nous recommandons de prendre rendez-vous pour éviter l'attente et garantir la disponibilité des pièces pour votre modèle d'iPhone. Appelez-nous au 07.73.60.15.21 ou prenez rendez-vous en ligne." },
  { q: "Intervenez-vous à domicile pour réparer un iPhone ?", r: "Oui, nous proposons un service de réparation iPhone à domicile à Saint-Chamond et dans les communes environnantes. Contactez-nous pour vérifier si votre adresse est couverte et pour connaître les conditions." },
  { q: "Mes données seront-elles conservées après la réparation ?", r: "Oui, toutes vos données (photos, contacts, applications, messages) restent intactes lors d'une réparation matérielle (écran, batterie, connecteur). Nous ne touchons jamais aux données de votre iPhone. Cependant, nous vous recommandons toujours de faire une sauvegarde iCloud ou iTunes avant toute réparation par précaution." },
];

const AVIS_PAGE = [
  { text: "Mon iPhone 13 avait l'écran complètement cassé. Suro Phone l'a réparé en moins d'une heure avec un écran de très bonne qualité. Service rapide et professionnel, je recommande !", name: "Sophie M.", ville: "Saint-Chamond" },
  { text: "Batterie de mon iPhone 12 changée le jour même. Maintenant mon téléphone tient toute la journée ! Prix correct et garantie de 3 mois. Parfait !", name: "Thomas L.", ville: "Saint-Chamond" },
  { text: "Mon iPhone XR ne chargeait plus du tout. Le connecteur a été remplacé rapidement et fonctionne parfaitement. Excellent service, merci !", name: "Marie D.", ville: "Saint-Chamond" },
];

const ReparationIPhoneSaintChamond = () => {
  return (
    <div className="min-h-screen bg-white">
      <SeoNav />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1a2332] via-[#1e2a3d] to-[#1a2332] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Réparation iPhone Saint-Chamond
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Expert en réparation iPhone à Saint-Chamond – Intervention rapide, pièces premium et garantie 3 mois
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a href="tel:0773601521" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-green-500 hover:bg-green-600 text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl">
                <Phone className="w-5 h-5 mr-2" />
                Appeler maintenant
              </Button>
            </a>
            <Link to="/rendez-vous" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl">
                Prendre rendez-vous
              </Button>
            </Link>
            <a href="https://wa.me/33773601521" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-[#25D366] hover:bg-[#20BD5A] text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Clock className="w-8 h-8 text-blue-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">30-60 min</div>
              <div className="text-sm text-gray-300">Réparation rapide</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">3 mois</div>
              <div className="text-sm text-gray-300">Garantie</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Wrench className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">Premium</div>
              <div className="text-sm text-gray-300">Pièces d'origine</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Star className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">+1000</div>
              <div className="text-sm text-gray-300">Clients satisfaits</div>
            </div>
          </div>
        </div>
      </section>

      {/* SEO Content Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Votre expert en réparation iPhone à Saint-Chamond</h2>
            
            <p className="text-gray-700 mb-4">
              Vous cherchez un <strong>réparateur iPhone fiable à Saint-Chamond</strong> ? Suro Phone est votre partenaire de confiance pour toutes les réparations de votre iPhone, quel que soit le modèle. Que vous possédiez un iPhone 15, iPhone 14, iPhone 13, iPhone 12, iPhone 11, iPhone XR, iPhone SE ou même des modèles plus anciens comme l'iPhone 8 ou iPhone 7, nous intervenons rapidement pour redonner vie à votre smartphone.
            </p>

            <p className="text-gray-700 mb-4">
              Basés à Saint-Chamond, nous sommes spécialisés dans la <strong>réparation rapide d'iPhone</strong> avec des délais d'intervention de 30 à 60 minutes pour la plupart des pannes. Notre atelier est équipé des outils les plus performants et nos techniciens sont formés aux dernières techniques de réparation Apple. Nous utilisons exclusivement des <strong>pièces détachées premium</strong> testées avant et après installation pour garantir la qualité de nos interventions.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Les réparations iPhone les plus courantes à Saint-Chamond</h3>
            
            <p className="text-gray-700 mb-4">
              Chez Suro Phone, nous traitons quotidiennement tous les types de pannes iPhone. Les interventions les plus fréquentes incluent le <strong>remplacement d'écran iPhone cassé</strong> (LCD, OLED ou écran d'origine selon votre modèle), le <strong>changement de batterie iPhone</strong> pour retrouver une autonomie optimale, la réparation de connecteur de charge lorsque votre iPhone ne charge plus, et le remplacement de caméra avant ou arrière.
            </p>

            <p className="text-gray-700 mb-4">
              Nous intervenons également sur des problèmes plus techniques comme les bugs logiciels, les problèmes de reconnaissance Face ID ou Touch ID, les pannes de haut-parleur ou de micro, et même les dégâts des eaux. Chaque <strong>réparation iPhone à Saint-Chamond</strong> est accompagnée d'une garantie de 3 mois pour votre tranquillité d'esprit.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Pourquoi choisir Suro Phone pour votre réparation iPhone ?</h3>
            
            <CheckList items={[
              { titre: "Intervention rapide :", texte: "95% de nos réparations iPhone sont effectuées le jour même, en 30 à 60 minutes selon la panne." },
              { titre: "Pièces premium :", texte: "Nous utilisons uniquement des écrans OLED de haute qualité, des batteries haute capacité et des composants testés." },
              { titre: "Garantie 3 mois :", texte: "Toutes nos réparations iPhone sont garanties 3 mois pièces et main d'œuvre." },
              { titre: "Prix transparents :", texte: "Devis gratuit et sans frais cachés." },
              { titre: "Service à domicile :", texte: "Nous pouvons intervenir directement chez vous à Saint-Chamond et alentours." },
              { titre: "Expertise reconnue :", texte: "Plus de 1000 réparations iPhone réalisées avec un taux de satisfaction client de 98%." },
            ]} />

            <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Comment se déroule une réparation iPhone chez Suro Phone ?</h3>
            
            <p className="text-gray-700 mb-4">
              Le processus est simple et rapide. Contactez-nous par téléphone au <strong>07.73.60.15.21</strong>, via WhatsApp ou en prenant rendez-vous en ligne. Nous établissons un diagnostic précis de la panne et vous proposons un devis transparent. Une fois accepté, nous commandons les pièces si nécessaire et intervenons rapidement, généralement le jour même.
            </p>

            <p className="text-gray-700 mb-4">
              Pendant la réparation, nos techniciens prennent soin de votre iPhone et testent minutieusement chaque fonctionnalité après l'intervention. Vous récupérez votre iPhone comme neuf avec sa garantie de 3 mois. Nous vous conseillons également sur l'entretien de votre appareil pour éviter les pannes futures.
            </p>

            <p className="text-gray-700 mb-4">
              Pour une <strong>réparation iPhone rapide et professionnelle à Saint-Chamond</strong>, faites confiance à Suro Phone. Nous sommes disponibles du lundi au samedi et intervenons dans tout Saint-Chamond et les communes environnantes. N'attendez plus pour redonner vie à votre iPhone !
            </p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Nos services de réparation iPhone</h2>
            <p className="text-lg text-gray-600">Tous les modèles d'iPhone réparés à Saint-Chamond</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="text-blue-500 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Écran iPhone cassé</h3>
                <p className="text-gray-600 text-center mb-4">
                  Remplacement d'écran LCD, OLED ou original pour tous les modèles d'iPhone. Réparation en 30-60 minutes.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Écran OLED premium</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Test complet après pose</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Garantie 3 mois</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="text-green-500 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Batterie iPhone HS</h3>
                <p className="text-gray-600 text-center mb-4">
                  Changement de batterie iPhone pour retrouver 100% d'autonomie. Batteries haute capacité garanties.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Batterie haute capacité</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Réparation en 30 min</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Garantie 3 mois</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="text-purple-500 mb-4">
                  <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center">Autres réparations</h3>
                <p className="text-gray-600 text-center mb-4">
                  Connecteur de charge, caméra, haut-parleur, micro, boutons, dégâts des eaux et plus encore.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Tous modèles iPhone</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Diagnostic gratuit</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm text-gray-700">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <span>Devis transparent</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Link to="/rendez-vous">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white px-8 py-6 text-lg rounded-lg transition-colors">
                Demander un devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <AvisSection titre="Avis de nos clients" avis={AVIS_PAGE} sousTitre="Ce que nos clients disent de nos réparations iPhone" />

      <FAQSection titre="Questions fréquentes" items={FAQ} sousTitre="Réparation iPhone à Saint-Chamond" />

      <CtaSection titre="Besoin de réparer votre iPhone maintenant ?" texte="Contactez-nous pour un devis gratuit et une intervention rapide à Saint-Chamond" />

      <VillesVoisines slug="saint-chamond" />

      <SeoFooter texte="© 2026 Suro Phone - Réparation iPhone Saint-Chamond - Tous droits réservés" />
    </div>
  );
};

export default ReparationIPhoneSaintChamond;